import { FC, HTMLAttributes, useEffect, useState } from "react";
import classNames from "classnames";
import BlockedContainer from "../BlockedContainer";
import { RDCModal, RDCModalBody, RDCModalHeader } from "../RDCModal";
import { ReDocTrober } from "src/UI";
import { useAppDispatch, useAppSelector } from "src/Hooks";
import { ICertificateInfo } from "src/Types/Certificate";
import CertificateItem from "./CertificateItem";
import API from "src/API";
import { showModal, toggleShowHelpModal } from "src/Slicers/modalSlice";
import { ModalTypes } from "..";
import st from './CertificatesModal.module.scss';

interface ICertificatesModalProps extends HTMLAttributes<HTMLDivElement> {
	isOpen: boolean;
	title?: string;
	toggleOpen: () => void;
	onChangeCert: ( cert: ICertificateInfo ) => void;
	currentThumbprint: string;
	ignoreDenyUnqualifiedSign?: boolean;
};

const CertificatesModal: FC<ICertificatesModalProps> = ( props ) => {

	const { isOpen, title = "Выберите сертификат", currentThumbprint, onChangeCert, toggleOpen, className, ignoreDenyUnqualifiedSign = false, ...otherProps } = props;

	const dispatch = useAppDispatch();
	const pluginInfo = useAppSelector( state => state.pluginInfo?.source );
	const toggleShowHelp = () => dispatch( toggleShowHelpModal(false) );
	const [ isLoading, setIsLoading ] = useState<boolean>(false);
	const [ certList, setCertList ] = useState<ICertificateInfo[]>([]);
	const [ denyUnqualifiedSign, setDenyUnqualifiedSign ] = useState<boolean>(false);
	const [ qualifiedThumbprints, setQualifiedThumbprints ] = useState<Set<string>>(new Set());

	/** пытаемся распарсить JSON, возвращаем null при ошибке */
	const tryParseJSON = ( str: string ): unknown => {
		try { return JSON.parse( str ); } catch { return null; }
	};

	/** определяем значение флага DenyUnqualifiedSign из Redux, localStorage или API */
	const resolveDenyFlag = async (): Promise<boolean> => {
		if ( pluginInfo?.DenyUnqualifiedSign !== undefined ) {
			return Boolean( pluginInfo.DenyUnqualifiedSign );
		}
		
		const windowWithRDC = window as { RDC?: { localStorage?: { getItem?: ( key: string ) => string } } };
		const raw = windowWithRDC?.RDC?.localStorage?.getItem?.("plugins");
		if ( raw ) {
			const parsed = tryParseJSON( raw ) as { DenyUnqualifiedSign?: boolean } | null;
			if ( parsed?.DenyUnqualifiedSign !== undefined ) return Boolean( parsed.DenyUnqualifiedSign );
		}
		
		const plugins = await API.Plugins.get().catch( () => null );
		if ( plugins?.DenyUnqualifiedSign !== undefined ) return Boolean( plugins.DenyUnqualifiedSign );
		
		return false;
	};

	useEffect(() => {
		/** Функция получения списка сертификатов  */
		const getCertList = async () => {
			setIsLoading(true);
			try {
				const deny = ignoreDenyUnqualifiedSign ? false : await resolveDenyFlag();
				setDenyUnqualifiedSign( deny );
				const certs = await API.Certificate.getCertList();
				let qualified = new Set<string>();
				if ( deny ) {
					qualified = await API.Certificate.getQualifiedThumbprints();
					setQualifiedThumbprints( qualified );
				} else {
					setQualifiedThumbprints( new Set() );
				}
				const filtered = deny
					? certs.filter( cert => qualified.has( cert.thumbprint ) )
					: certs;
				const sorted = filtered.sort( ( a, b ) => b.validToDate.getTime() - a.validToDate.getTime() );
				setCertList( 
					sorted.sort( ( a ) => a.isValid ? -1 : 0 )
				);
			}
			catch 
			{
				toggleOpen();
				dispatch(showModal({
					type: ModalTypes.error,
					title: "Ошибка",
					text: "Ошибка при работе с плагином КриптоПро"
				}));
			}
			finally {
				setIsLoading(false);
			}
		};
		if ( isOpen ) {
			getCertList();
		}
	// eslint-disable-next-line
	}, [isOpen]);

	/** Вызывается при клике по сертификату  */
	const onCertificateClickHandler = ( cert: ICertificateInfo ) => {
		if ( denyUnqualifiedSign ) {
			if ( qualifiedThumbprints.size === 0 || !qualifiedThumbprints.has( cert.thumbprint ) ) {
				dispatch(showModal({
					type: ModalTypes.error,
					title: "Ошибка",
					text: "Выбранный сертификат не подходит для подписи. Пожалуйста, выберите другой сертификат"
				}));
				return;
			}
		}
		// Если сертификат валидны, вызываем функцию логина из пропсов
		if ( cert.isDateValid && cert.isValid ) {
			onChangeCert(cert);
			toggleOpen();
		}
	};

	const renderCert = ( cert: ICertificateInfo ) => (
		<CertificateItem
			key={ "cert_" + cert.thumbprint}
			certificate={cert} 
			currentThumbprint={currentThumbprint}
			onCertificateClick={onCertificateClickHandler}
			clickHelp={toggleShowHelp}
		/>
	);

	/** Рендер списка сертификатов  */
	const renderCertList = () => (
		<div className={st.scrollable_container}>
 			<ul className={st.certificate_list}>
 				{ certList.map( renderCert ) }
			</ul>
		</div>
	);

	const renderContent = () => {
		if( isLoading ) {
			return (
				<div className="centered_content">
					<ReDocTrober size='xl' />
				</div>
			);
		}
		else 
			return renderCertList();
	};

	if ( !isOpen ) return null;

	return (
		<BlockedContainer isFullScreen onRequestClose={toggleOpen}>
			<RDCModal id="rdc_select-cert" className={classNames(className, st.certificateModal, 'light')} { ...otherProps }>
				<RDCModalHeader onClose={toggleOpen}>{title}</RDCModalHeader>
				<RDCModalBody className={st.certificateModal__body}>
					{ renderContent() }
				</RDCModalBody>
			</RDCModal>
		</BlockedContainer>
	);
};

export default CertificatesModal;
